import {inject, Injectable} from '@angular/core';
import {ImportOrder} from '../models/import-order.model';
import {
  collection,
  deleteDoc,
  doc,
  Firestore, getDocs,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  where
} from '@angular/fire/firestore';
import {BehaviorSubject} from "rxjs";
import {Functions, httpsCallable} from "@angular/fire/functions";
import {DatabaseProduct} from "../models/database-product.model";
import {PrintoclockVariant} from "../models/printoclock-variant.model";
import {PrintoclockOrder} from "../models/printoclock-order.model";

@Injectable({
  providedIn: 'root',
})
export class PrintoclockService {
  private unsubscribeImportOrders: Unsubscribe;
  private importOrders$: BehaviorSubject<ImportOrder[]>;
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  /**
   * Get imported orders from PrintOCLock sorted by date desc
   * @returns The query to get imports
   */
  public getImports(): BehaviorSubject<ImportOrder[]> {
    if (!this.importOrders$) {
      this.importOrders$ = new BehaviorSubject<ImportOrder[]>([]);
      const q = query(
        collection(this.firestore, 'imports'),
        orderBy('date', 'desc'))
        .withConverter(ImportOrder.converter);
      this.unsubscribeImportOrders = onSnapshot(q, snapshot => {
        if (snapshot.empty) {
          return this.importOrders$.next([]);
        }
        this.importOrders$.next(snapshot.docs.map(doc => doc.data()));
      })
    }
    return this.importOrders$;
  }

  /**
   * Search imports by exact reference match.
   * @param reference The reference to search for
   * @returns Matching import orders
   */
  async searchByReference(reference: number): Promise<ImportOrder[]> {
    const q = query(
      collection(this.firestore, 'imports'),
      where('reference', '==', reference)
    ).withConverter(ImportOrder.converter);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Delete an imported order
   * @param id The id of the import
   * @returns void
   */
  public delete(id: string): Promise<void> {
    return deleteDoc(doc(this.firestore, "imports", id));
  }

  /**
   * Initiates the import process with the given order and organization ID.
   * @param order - The import order containing the details required for the import process.
   * @param organizationId - The unique identifier of the organization for which the import is being initiated.
   * @returns A promise that resolves when the import process is successfully started.
   */
  async startImport(order: ImportOrder, organizationId: string): Promise<void> {
    return httpsCallable<{ order: ImportOrder, organizationId: string }, void>
    (this.functions, 'push_imports_gen2')({order, organizationId})
      .then(res => res.data);
  }

  /**
   * Call API to print production file
   * @param order The order to print
   * @returns void
   */
  public print(order: ImportOrder): Promise<{ html: string }> {
    return httpsCallable<{ reference: string }, { html: string }>
    (this.functions, 'printoclock_order_print_gen2')({reference: order.id})
      .then(res => res.data);
  }

  /**
   * Fetches an order based on the provided reference and account ID.
   * @param reference - The unique reference identifier for the order.
   * @param accountId - The identifier of the account associated with the order.
   * @returns A promise that resolves when the order retrieval is complete.
   */
  async getOrder(reference: string, accountId: string): Promise<void> {
    return httpsCallable<{ reference: string, accountId: string }, void>
    (this.functions, 'get_printoclock_order_gen2')({reference, accountId})
      .then(res => res.data);
  }

  /**
   * Call API get available Printoclock products
   * @returns All available printoclock products
   */
  async getProducts(): Promise<DatabaseProduct[]> {
    const q = query(collection(this.firestore, 'database'))
      .withConverter(DatabaseProduct.converter);
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return [];
    }
    return snapshot.docs.map(doc => doc.data());
  }

  /**
   * Retrieves product details for a given product code.
   * @param code - The unique code representing the product to fetch details for.
   * @returns A promise that resolves to the product details.
   */
  async getProductDetails(code: string): Promise<DatabaseProduct> {
    return httpsCallable<{ code: string }, DatabaseProduct>
    (this.functions, 'get_database_product_gen2')({code})
      .then(res => res.data);
  }

  /**
   * Retrieves product details for a given product code.
   * @param code - The unique code representing the product to fetch details for.
   * @param quantity - The custom quantity
   * @param width - The custom width
   * @param height - The custom height
   * @returns A promise that resolves to the product details.
   */
  async getVariantDetails(code: string, quantity: number, width: number, height: number)
    : Promise<PrintoclockVariant[]> {
    return httpsCallable<{ code: string, quantity: number, width: number, height: number }, PrintoclockVariant[]>
    (this.functions, 'get_printoclock_variant_gen2')({code, quantity, width, height})
      .then(res => res.data);
  }

  /**
   * Retrieves a list of Printoclock orders by invoking a Firebase callable cloud function.
   * @returns A promise that resolves to an array of PrintoclockOrder objects.
   */
  async getOrders(): Promise<PrintoclockOrder[]> {
    return httpsCallable<null, PrintoclockOrder[]>(this.functions, 'get_printoclock_orders_gen2')()
      .then(res => res.data);
  }

  /**
   * Unsubscribes from a previously subscribed observable or event listener.
   * If an unsubscribe function exists, it will be called to clean up the subscription and reset associated properties.
   */
  unsubscribe(): void {
    if (this.unsubscribeImportOrders) {
      this.unsubscribeImportOrders();
      this.importOrders$ = null;
    }
  }
}
